/**
 * iter119 — Amblemead B2B marketing landing page.
 *
 * Served ONLY when the hostname matches amblemead.biz / www.amblemead.biz
 * (see HostGate wiring in App.js). Every other route on this host is
 * redirected to `/`, so this file is effectively the entire product
 * surface for the marketing domain.
 *
 * Palette (26/27 season refresh — dark forest + electric lime):
 *   Deep Forest #0F766E
 *   Deeper      #0B5651
 *   Lime        #F18D49
 *   Lime Hover  #F5A468
 *   Pitch Green #134E4A
 *   Cream       #FAFAF9
 */
import { useEffect, useRef, useState } from "react";
import {
  Trophy,
  Users,
  Timer,
  Sparkle,
  DeviceMobile,
  ChartLine,
  ArrowRight,
  ArrowUpRight,
  CheckCircle,
  Check,
  SquaresFour,
  X,
} from "@phosphor-icons/react";
import { toast, Toaster } from "sonner";
import { api, formatApiError } from "@/lib/api";

const HERO_IMG = "https://images.unsplash.com/photo-1661632359980-9896d88b9b7b?crop=entropy&cs=srgb&fm=jpg&q=80&w=1600";
const APP_HOST = "https://play.amblemead.biz";

const NAV = [
  { id: "features", label: "Features" },
  { id: "how", label: "Roadmap" },
  { id: "demo", label: "Request more information" },
];

const STATS = [
  { value: "Weekly Leagues", label: "6 predictions for each match", detail: "6 Football fixtures per league" },
  { value: "60 seconds", label: "To place a prediction", detail: "No app download. No friction." },
  { value: "£££", label: "Weekly prize pot", detail: "Cash, vouchers or a pint on the house." },
];

const STEPS = [
  { step: "01", title: "Log in", body: "Players sign in to your branded pub league in one tap. No app to download." },
  { step: "02", title: "Make predictions", body: "Predict 6 events across 2 matches on each match day, 3 match days in each league." },
  { step: "03", title: "Submit", body: "Predictions lock at kick-off. Sit back and cheer your calls home." },
  { step: "04", title: "Results email", body: "Each player gets a results email with match-day standing once the results are in." },
  { step: "05", title: "Weekly league completes", body: "Points are automatically calculated and leagues updates on the website." },
  { step: "06", title: "Final standings email", body: "All players receive a league-standings email — top of the table, gaps to close, mates to beat." },
  { step: "07", title: "Winners notified", body: "The top finishers are automatically notified of their prize with a QR-code voucher." },
  { step: "08", title: "Claim at your pub", body: "Winners walk in, scan the QR at the bar, claim their prize. Instant footfall you can measure." },
];

const FEATURES = [
  {
    icon: DeviceMobile,
    title: "No app to download",
    body: "Pure web — works on any phone, any browser. Players are in within 30 seconds of scanning your QR.",
    span: "md:col-span-8",
    tone: "amber",
  },
  {
    icon: Trophy,
    title: "Weekly prizes",
    body: "You choose the prize, we automate the winner.",
    span: "md:col-span-4",
    tone: "green",
  },
  {
    icon: ChartLine,
    title: "Live Premier-League scores",
    body: "Fed straight from official feeds. No manual result entry.",
    span: "md:col-span-4",
    tone: "green",
  },
  {
    icon: Users,
    title: "Mini-leagues",
    body: "Groups of regulars, friends, families — private tables inside your main league.",
    span: "md:col-span-8",
    tone: "amber",
  },
  {
    icon: Sparkle,
    title: "Full branding",
    body: "Your venue's colours, badge, tone. Amblemead runs quietly behind the curtain.",
    span: "md:col-span-6",
    tone: "amber",
  },
  {
    icon: Timer,
    title: "Editor dashboard",
    body: "One screen to run everything — matches, results, payouts, WhatsApp broadcasts.",
    span: "md:col-span-6",
    tone: "green",
  },
];

function ScrollLink({ id, children, className = "", testid }) {
  return (
    <a
      href={`#${id}`}
      data-testid={testid}
      onClick={(e) => {
        e.preventDefault();
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }}
      className={className}
    >
      {children}
    </a>
  );
}

function PrimaryButton({ onClick, children, testid, className = "" }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-2 rounded-full bg-[#F18D49] px-6 py-3 text-sm font-semibold text-[#0F766E] tracking-wide transition-transform duration-300 ease-out hover:-translate-y-0.5 hover:bg-[#F5A468] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#F18D49]/60 ${className}`}
    >
      {children}
    </button>
  );
}

function DemoForm({ variant = "inline", onSubmitted }) {
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [postcode, setPostcode] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  async function onSubmit(e) {
    e.preventDefault();
    if (!name.trim() || !company.trim() || !email.trim() || !phone.trim()) {
      toast.error("Name, pub name, email and phone are required.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/marketing/demo-request", {
        name: name.trim(),
        company: company.trim(),
        postcode: postcode.trim(),
        email: email.trim(),
        phone: phone.trim(),
        message: message.trim(),
        source: `amblemead.biz/${variant}`,
      });
      setDone(true);
      toast.success("Thanks — Carl will be in touch shortly.");
      onSubmitted?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  }

  if (done) {
    return (
      <div
        className="rounded-2xl border border-[#134E4A]/40 bg-[#134E4A]/10 p-8 text-[#FAFAF9]"
        data-testid={`demo-form-success-${variant}`}
      >
        <CheckCircle weight="duotone" className="h-10 w-10 text-[#F8BC85]" />
        <div className="mt-4 font-editorial text-2xl">Cheers — we&apos;re on it.</div>
        <div className="mt-2 text-sm text-[#B4CBCA]">
          Carl will drop you a note within 24 hours. In the meantime, if
          you&apos;re already a customer, sign in at{" "}
          <a href={APP_HOST} className="text-[#F18D49] underline">play.amblemead.biz</a>.
        </div>
      </div>
    );
  }

  const inputBase =
    "w-full rounded-xl border border-black/10 bg-white px-4 py-3 text-sm text-[#0F766E] placeholder:text-[#6B7280] transition-colors duration-200 focus:border-[#F18D49] focus:outline-none focus:ring-2 focus:ring-[#F18D49]/40";

  return (
    <form
      onSubmit={onSubmit}
      className="space-y-4"
      data-testid={`demo-form-${variant}`}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Your name *"
          data-testid={`demo-form-name-${variant}`}
          className={inputBase}
          maxLength={120}
          required
        />
        <input
          type="text"
          value={company}
          onChange={(e) => setCompany(e.target.value)}
          placeholder="Pub name *"
          data-testid={`demo-form-company-${variant}`}
          className={inputBase}
          maxLength={200}
          required
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <input
          type="text"
          value={postcode}
          onChange={(e) => setPostcode(e.target.value)}
          placeholder="Post code"
          data-testid={`demo-form-postcode-${variant}`}
          className={inputBase}
          maxLength={20}
        />
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email *"
          data-testid={`demo-form-email-${variant}`}
          className={inputBase}
          required
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <input
          type="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="Phone *"
          data-testid={`demo-form-phone-${variant}`}
          className={inputBase}
          maxLength={40}
          required
        />
      </div>
      <textarea
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        rows={4}
        placeholder="Tell us a little about your venue or charity..."
        data-testid={`demo-form-message-${variant}`}
        className={`${inputBase} resize-none`}
        maxLength={2000}
      />
      <div className="flex items-center gap-4 pt-2">
        <PrimaryButton onClick={onSubmit} testid={`demo-form-submit-${variant}`}>
          {submitting ? "Sending…" : (
            <>Send <ArrowRight weight="bold" className="h-4 w-4" /></>
          )}
        </PrimaryButton>
        <div className="text-xs text-[#4B5563]">
          We reply within 24 hours. No spam, ever.
        </div>
      </div>
    </form>
  );
}

function DemoDialog({ open, onOpenChange }) {
  useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center p-4 sm:p-8"
      role="dialog"
      aria-modal="true"
      data-testid="demo-dialog"
    >
      <div
        className="absolute inset-0 bg-[#0F766E]/80 backdrop-blur-xl"
        onClick={() => onOpenChange(false)}
      />
      <div className="relative z-10 w-full max-w-2xl rounded-3xl border border-white/10 bg-[#0B5651] p-8 md:p-10 shadow-2xl overflow-y-auto max-h-[90vh]">
        <button
          onClick={() => onOpenChange(false)}
          className="absolute right-4 top-4 rounded-full p-2 text-[#B4CBCA] hover:bg-white/5 hover:text-[#FAFAF9]"
          aria-label="Close"
          data-testid="demo-dialog-close"
        >
          <X className="h-5 w-5" />
        </button>
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#F18D49] font-semibold">
          Request more information
        </div>
        <h2 className="font-heading text-2xl md:text-3xl text-[#FAFAF9] mt-3 mb-3 leading-tight font-bold">
          Use Football Challenge to improve your mid-week customer footfall and improve profitability.
        </h2>
        <p className="text-sm text-[#B4CBCA] mb-6 max-w-lg">
          Tell us a little bit about you and we will be in touch to
          discuss how we can work together to benefit your business.
        </p>
        <DemoForm variant="dialog" onSubmitted={() => setTimeout(() => onOpenChange(false), 2500)} />
      </div>
    </div>
  );
}

export default function AmblemeadLandingPage() {
  const [dialog, setDialog] = useState(false);
  const heroRef = useRef(null);

  return (
    <div className="bg-[#0F766E] text-[#FAFAF9] min-h-screen font-body" data-testid="amblemead-landing-page">
      <Toaster theme="dark" richColors position="top-center" />

      {/* ----------------------- Sticky glass header ----------------------- */}
      <header className="sticky top-0 z-50 border-b border-white/10 bg-[#0F766E]/60 backdrop-blur-xl">
        <div className="mx-auto max-w-7xl px-6 md:px-10 h-16 flex items-center justify-between">
          <a href="#top" className="flex items-center gap-3" data-testid="header-logo">
            <img
              src="/favicon.png"
              alt="Football Challenge logo"
              className="h-9 w-9 rounded-full bg-white/95 p-1 shadow-sm"
              data-testid="header-logo-img"
            />
            <div className="font-heading text-lg font-bold tracking-tight text-[#FAFAF9]">
              Football Challenge
            </div>
          </a>
          <nav className="hidden md:flex items-center gap-8">
            {NAV.map((n) => (
              <ScrollLink
                key={n.id}
                id={n.id}
                testid={`nav-${n.id}`}
                className="text-xs font-semibold uppercase tracking-[0.2em] text-[#B4CBCA] hover:text-[#FAFAF9] transition-colors"
              >
                {n.label}
              </ScrollLink>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <PrimaryButton onClick={() => setDialog(true)} testid="header-request-demo" className="px-4 py-2 text-xs">
              Request demo
            </PrimaryButton>
          </div>
        </div>
      </header>

      {/* ----------------------- Hero ----------------------- */}
      <section id="top" ref={heroRef} className="relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="h-full w-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0F766E]/70 via-[#0F766E]/60 to-[#0F766E]" />
        </div>
        <div className="relative mx-auto max-w-7xl px-6 md:px-10 py-32 md:py-40 grid md:grid-cols-12 gap-8">
          <div className="md:col-span-8">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-[10px] uppercase tracking-[0.3em] text-[#F18D49] font-semibold" data-testid="hero-eyebrow">
              <Sparkle weight="fill" className="h-3 w-3" />
              26 / 27 Season
            </div>
            <h1
              className="font-display mt-6 text-6xl md:text-8xl lg:text-9xl leading-[0.9] uppercase text-[#FAFAF9]"
              data-testid="hero-headline"
            >
              Where local pubs
              {" "}
              <span className="text-[#F18D49]">meet the challenge</span>
            </h1>
            <div className="mt-8 max-w-2xl text-lg md:text-xl leading-relaxed text-[#B4CBCA] space-y-4" data-testid="hero-sub">
              <p>
                Football Challenge provides you with your own branded
                prediction game, designed to create footfall on the quiet
                Monday to Thursday nights in your pub and increase your ROI.
              </p>
              <p className="pl-8">
                If you&apos;re a Not-for-Profit organisation this is a great
                way to engage with supporters and fundraise.
              </p>
              <p className="pl-16">
                Also a great way to team-build in any organisation.
              </p>
              <p>
                Fully managed by us, you have zero admin.
              </p>
            </div>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <PrimaryButton onClick={() => setDialog(true)} testid="hero-request-demo">
                Grow Your Business <ArrowRight weight="bold" className="h-4 w-4" />
              </PrimaryButton>
            </div>
          </div>
          <div className="hidden md:block md:col-span-4 relative">
            <div className="absolute -right-8 top-8 h-64 w-64 rounded-full bg-[#F18D49]/20 blur-3xl" />
            <div className="absolute right-16 bottom-8 h-40 w-40 rounded-full bg-[#134E4A]/30 blur-3xl" />
          </div>
        </div>
      </section>

      {/* ----------------------- Two Sides of the Same Match ----------------------- */}
      <section id="one-platform" className="border-b border-white/10 bg-[#F8FAFC] py-24 md:py-32" data-testid="two-sides-section">
        <div className="mx-auto max-w-7xl px-6 md:px-10">
          <div className="text-center max-w-4xl mx-auto">
            <h2
              className="font-display text-5xl md:text-7xl lg:text-8xl leading-[0.9] uppercase text-[#0F766E]"
              data-testid="two-sides-headline"
            >
              One Platform<br />Two sides of the same Game.
            </h2>
            <p className="mt-8 text-base md:text-lg leading-relaxed text-[#1F2937] max-w-2xl mx-auto" data-testid="two-sides-sub">
              Your pub gets a fully managed, promotional platform. Customers
              and supporters get a free engaging, competitive, rewarding
              game.
            </p>
          </div>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* For Your Pub (dark) */}
            <div
              className="group relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#0F766E] via-[#0F766E] to-[#0B5651] p-10 md:p-12 shadow-lg transition-transform duration-300 hover:-translate-y-1"
              data-testid="two-sides-card-business"
            >
              <div className="absolute -right-16 -top-16 h-64 w-64 rounded-full bg-[#F18D49]/10 blur-3xl pointer-events-none" />
              <div className="flex items-center gap-4">
                <SquaresFour weight="bold" className="h-10 w-10 text-[#F18D49] shrink-0" />
                <h3 className="font-display text-3xl md:text-4xl uppercase text-[#FAFAF9]">For Your Pub</h3>
              </div>
              <p className="mt-6 text-[#E0F2F1] leading-relaxed max-w-md">
                Our Football Challenge will take your business to a new
                level of promotions — improving customer footfall and
                profitability — delivered without you lifting a finger.
              </p>
              <ul className="mt-8 space-y-3 text-[#E0F2F1]">
                {[
                  "Your brand, and image, protected and enhanced",
                  "Fully own-branded from start to finish",
                  "Zero admin — fully managed for you",
                  "Minimal staff involvement",
                  "Live KPI dashboards, site & head office",
                ].map((b) => (
                  <li key={b} className="flex items-start gap-3" data-testid={`biz-bullet-${b.split(" ")[0].toLowerCase()}`}>
                    <Check weight="bold" className="h-4 w-4 text-[#F18D49] mt-1 shrink-0" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
              <a
                href="#demo"
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById("demo")?.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
                className="mt-10 inline-flex items-center gap-2 text-sm font-semibold text-[#F18D49] hover:text-[#F5A468] transition-colors"
                data-testid="two-sides-cta-business"
              >
                Explore the business platform <ArrowUpRight weight="bold" className="h-4 w-4" />
              </a>
            </div>

            {/* For Fans (light) */}
            <div
              className="group relative overflow-hidden rounded-3xl bg-white p-10 md:p-12 shadow-md border border-black/5 transition-transform duration-300 hover:-translate-y-1"
              data-testid="two-sides-card-fans"
            >
              <div className="absolute -right-16 -top-16 h-64 w-64 rounded-full bg-[#F18D49]/25 blur-3xl pointer-events-none" />
              <div className="flex items-center gap-4">
                <Trophy weight="bold" className="h-10 w-10 text-[#0F766E] shrink-0" />
                <h3 className="font-display text-3xl md:text-4xl uppercase text-[#0F766E]">For Fans</h3>
              </div>
              <p className="mt-6 text-[#1F2937] leading-relaxed max-w-md">
                Fun, engaging and competitive. Customer retention is proven
                to be high.
              </p>
              <ul className="mt-8 space-y-3 text-[#1F2937]">
                {[
                  "Free predictions of events in 6 matches every week.",
                  "Leagues & mini-leagues with mates",
                  "Chance to win discounts on meals and drinks",
                  "Receive email reminders of kick-off times and league standings.",
                  "Option to join the local WhatsApp banter group.",
                ].map((b) => (
                  <li key={b} className="flex items-start gap-3" data-testid={`fan-bullet-${b.split(" ")[0].toLowerCase()}`}>
                    <Check weight="bold" className="h-4 w-4 text-[#0F766E] mt-1 shrink-0" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
              <a
                href="/business/demo?demo=1"
                className="mt-10 inline-flex items-center gap-2 text-sm font-semibold text-[#0F766E] hover:text-[#134E4A] transition-colors"
                data-testid="two-sides-cta-fans"
              >
                Explore the fan experience <ArrowUpRight weight="bold" className="h-4 w-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ----------------------- Features grid (Bento) ----------------------- */}
      <section id="features" className="border-b border-white/10 py-24 md:py-32">
        <div className="mx-auto max-w-7xl px-6 md:px-10">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-14">
            <div className="max-w-2xl">
              <div className="text-[10px] uppercase tracking-[0.3em] font-semibold text-[#F18D49]">01 · Everything you need</div>
              <h2 className="font-display mt-4 text-5xl md:text-6xl leading-[0.95] uppercase text-[#FAFAF9]">
                Simple for players. Powerful for you.
              </h2>
            </div>
            <p className="text-[#B4CBCA] max-w-sm text-base leading-relaxed">
              Every feature Amblemead ships is measured by one question: does
              it get regulars back into the bar next Saturday?
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
            {FEATURES.map((f) => {
              const Icon = f.icon;
              const accent = f.tone === "amber" ? "#F18D49" : "#134E4A";
              return (
                <div
                  key={f.title}
                  className={`${f.span} col-span-12 rounded-2xl border border-white/10 bg-[#0B5651] p-8 transition-colors duration-300 hover:border-white/20 group`}
                  data-testid={`feature-${f.title.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <Icon weight="duotone" className="h-8 w-8 mb-6" style={{ color: accent }} />
                  <h3 className="font-editorial text-2xl text-[#FAFAF9] mb-3 tracking-tight">{f.title}</h3>
                  <p className="text-[#B4CBCA] leading-relaxed">{f.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ----------------------- Roadmap ----------------------- */}
      <section id="how" className="border-b border-white/10 py-24 md:py-32">
        <div className="mx-auto max-w-7xl px-6 md:px-10">
          <div className="max-w-2xl">
            <div className="text-[10px] uppercase tracking-[0.3em] font-semibold text-[#F18D49]">02 · Roadmap</div>
            <h2 className="font-display mt-4 text-5xl md:text-6xl leading-[0.95] uppercase text-[#FAFAF9]">
              A player&apos;s journey.
            </h2>
            <p className="mt-6 text-[#B4CBCA] leading-relaxed max-w-xl">
              Eight simple steps from log-in to prize in hand — every one of
              them automated so you spend zero minutes on admin.
            </p>
          </div>
          <div className="mt-20 relative" data-testid="roadmap">
            <ol className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
              {STEPS.map((s, i) => {
                const endOfRow = (i + 1) % 4 === 0;
                const isLast = i === STEPS.length - 1;
                return (
                  <li
                    key={s.step}
                    className="relative flex flex-col"
                    data-testid={`roadmap-step-${s.step}`}
                  >
                    {/* Horizontal connector line to the next card on lg+ */}
                    {!endOfRow && !isLast && (
                      <div
                        aria-hidden="true"
                        className="hidden lg:block absolute top-6 left-full w-8 h-px bg-gradient-to-r from-[#F18D49]/60 to-[#F18D49]/20 -translate-x-0"
                      />
                    )}
                    {/* Step badge */}
                    <div className="flex items-center gap-3 mb-4">
                      <span className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-[#F18D49] text-[#0F766E] font-display text-lg shadow-[0_0_0_6px_rgba(241,141,73,0.22)]">
                        {s.step}
                      </span>
                      <div className="flex-1 h-px bg-white/10" />
                    </div>
                    {/* Card body */}
                    <div className="rounded-2xl border border-white/10 bg-[#0B5651] p-6 flex-1 transition-colors duration-300 hover:border-[#F18D49]/40">
                      <h3 className="font-display text-xl md:text-2xl uppercase text-[#FAFAF9] leading-tight mb-3">
                        {s.title}
                      </h3>
                      <p className="text-[#B4CBCA] leading-relaxed text-base">
                        {s.body}
                      </p>
                    </div>
                  </li>
                );
              })}
            </ol>
          </div>
        </div>
      </section>

      {/* ----------------------- By the Numbers ----------------------- */}
      <section className="border-b border-white/10 bg-[#0F766E]">
        <div className="mx-auto max-w-7xl px-6 md:px-10 grid grid-cols-1 md:grid-cols-3">
          {STATS.map((s, i) => (
            <div
              key={s.value}
              className={`py-12 px-4 md:px-8 ${i < STATS.length - 1 ? "md:border-r border-white/10" : ""}`}
              data-testid={`stat-${i}`}
            >
              <div className="font-display text-4xl md:text-5xl leading-[0.95] text-[#F18D49] uppercase">{s.value}</div>
              <div className="mt-3 text-xs uppercase tracking-[0.25em] font-semibold text-[#FAFAF9]">{s.label}</div>
              <div className="mt-2 text-sm text-[#B4CBCA]">{s.detail}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ----------------------- Who it's for ----------------------- */}
      {/* ----------------------- Demo CTA ----------------------- */}
      <section id="demo" className="py-24 md:py-32 border-b border-white/10">
        <div className="mx-auto max-w-7xl px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <div className="text-[10px] uppercase tracking-[0.3em] font-semibold text-[#F18D49]">03 · Request more information</div>
            <h2 className="font-heading mt-4 text-3xl md:text-4xl leading-tight font-bold text-[#FAFAF9]">
              Use Football Challenge to improve your mid-week customer footfall and improve profitability.
            </h2>
            <p className="mt-6 text-[#B4CBCA] leading-relaxed max-w-md">
              Send us your contact information and this is the first step
              in our working together to benefit your business.
            </p>
            <div className="mt-8 flex items-center gap-3">
              <img src="/carl.jpg" alt="Carl · Football Challenge" className="h-14 w-14 rounded-full object-cover border-2 border-[#F18D49]/60" />
              <div>
                <div className="text-[#FAFAF9] font-semibold text-sm">Carl · Football Challenge</div>
                <div className="text-[#B4CBCA] text-xs">Replies within 24 hours, personally.</div>
              </div>
            </div>
          </div>
          <div className="md:col-span-7">
            <div className="rounded-3xl border border-white/10 bg-[#0B5651] p-8 md:p-10">
              <DemoForm variant="footer" />
            </div>
          </div>
        </div>
      </section>

      {/* ----------------------- Footer ----------------------- */}
      <footer className="py-20 md:py-24">
        <div className="mx-auto max-w-7xl px-6 md:px-10">
          <div className="border-t border-white/10 pt-16">
            <div className="mb-16">
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] font-semibold text-[#4B5563]">Legal</div>
                <div className="flex gap-4 mt-2">
                  <a href={`${APP_HOST}/terms`} className="text-sm text-[#B4CBCA] hover:text-[#FAFAF9]">Terms</a>
                  <a href={`${APP_HOST}/privacy`} className="text-sm text-[#B4CBCA] hover:text-[#FAFAF9]">Privacy</a>
                  <a href={`${APP_HOST}/cookies`} className="text-sm text-[#B4CBCA] hover:text-[#FAFAF9]">Cookies</a>
                </div>
              </div>
            </div>
            <div className="font-display text-[18vw] md:text-[16vw] leading-none uppercase text-[#FAFAF9]/5 select-none">
              Football Challenge
            </div>
            <div className="text-base md:text-lg text-[#B4CBCA] mt-8">
              © {new Date().getFullYear()} Amblemead Limited t/a Football Challenge. All rights reserved.
            </div>
          </div>
        </div>
      </footer>

      <DemoDialog open={dialog} onOpenChange={setDialog} />
    </div>
  );
}
